import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  listScenarios, createScenario, deleteScenario, updateScenario, snapshotScenario,
  duplicateScenario,
  resetScenario,
  listRevenues, listVariableExpenses,
  listOneOffExpenses,
  listPayrollChanges, listBonuses, listSocialRates, listInsurances,
  listOpeningPositions,
} from '../../api/treasury';
import { listIndirectCosts } from '../../api/indirectCosts';
import { listHrContracts, peopleCostSynthesis, listPayrollMovements, listHrLppRates, listHrAnnualExtras } from '../../api/hr';
import { getChargesSociales } from '../../api/charges_sociales';
import { getBsView } from '../../api/financials_bs';
import { scenarioToApiCreate, startFromLastClosed } from './engine/adapters';
import { resolveOpeningPositions } from './engine/openingPositions';
import { computeCashModel } from './engine/cashModel';
import { loadCashForecastData, computeForecastSeries } from './engine/cashForecastLoader';
import { extraMonths } from '../hr/salaryMass';
import ScenarioBar from './components/ScenarioBar';
import SynthesisTab from './tabs/SynthesisTab';
import RevenuesTab from './tabs/RevenuesTab';
import PurchasesTab from './tabs/PurchasesTab';
import StartingStepper from './tabs/StartingStepper';
import { OPENING_DEF, openingToLines } from './tabs/OpeningPositionsStep';
import PersonnelProjectionView from './tabs/PersonnelProjectionView';
import PersonnelManualForm, { PERSONNEL_DEF, computeManualPersonnel } from './tabs/PersonnelManualForm';
import SocialPhasingTab from './tabs/SocialPhasingTab';
import PageShell from '../../components/PageShell';
import Tabs from '../../components/Tabs';
import { usePersistentValue } from '../../hooks/usePersistentState';
import { resolveTabs } from '../../i18n/resolveTabs';
import { useP } from '../../i18n/useP';
import './TreasuryPage.css';

// Onglets de la section « Plan des liquidités », dans l'ordre voulu. Les 3
// derniers (point de départ, charges indirectes, charges sociales) sont
// remontés de l'ex-page « Point de départ » lors de l'absorption dans Finance.
// Ordre de construction (gauche → droite) : intrants → plan → graphique.
const CASH_TAB_DEFS = [
  { id: 'starting',        labelKey: 'scenarii:treasury.tabs.starting' },
  { id: 'personnel',       labelKey: 'scenarii:treasury.tabs.personnel' },
  { id: 'purchases',       labelKey: 'scenarii:treasury.tabs.purchases' },
  { id: 'revenues',        labelKey: 'scenarii:treasury.tabs.revenues' },
  { id: 'detail',          labelKey: 'scenarii:treasury.tabs.detail' },
  { id: 'synthesis',       labelKey: 'scenarii:treasury.tabs.synthesis' },
];

export default function TreasuryPage({ activeTenant, addToast, activeTab: activeTabProp, onTabChange }) {
  const { t } = useTranslation();
  const p = useP();
  // Onglets au format <Tabs> (id/label), résolus à la langue courante.
  const TAB_ITEMS = resolveTabs(CASH_TAB_DEFS, t);
  const tenantId = activeTenant?.id;
  // Onglet/sous-page contrôlé par le menu de gauche (App → cashTab). Plus de
  // barre d'onglets interne ni d'état local.
  const activeTab = activeTabProp || 'synthesis';

  const [scenarios, setScenarios] = useState([]);
  const [activeId, setActiveId]   = useState(null);
  const [scenario, setScenario]   = useState(null);
  // Comparaison de courbes (vue Total) : jusqu'à 2 autres scénarios dont on
  // superpose la seule courbe de solde. Chargement paresseux + cache par id.
  const [compareIds, setCompareIds]       = useState([]);
  const [compareSeries, setCompareSeries] = useState([]);
  const compareCacheRef = useRef({});   // { scenarioId: number[] soldes }

  const [revenues, setRevenues]               = useState([]);
  const [variables, setVariables]             = useState([]);
  const [indirectCosts, setIndirectCosts]     = useState([]);
  const [oneOff, setOneOff]                   = useState([]);
  const [payrollChanges, setPayrollChanges]   = useState([]);
  const [bonuses, setBonuses]                 = useState([]);
  const [socialRates, setSocialRates]         = useState([]);
  const [insurances, setInsurances]           = useState([]);
  // Durée de projection (1..12 mois) — attribut PROPRE au scénario (`scenario.horizon`,
  // persisté serveur). État local optimiste pour la fluidité du curseur ; resynchronisé
  // à chaque changement de scénario (voir effet plus bas) et écrit via PATCH.
  const [horizon, setHorizonLocal] = useState(12);
  // Mode de planification personnel, par scénario : 'people' (connecté) | 'manual'.
  const [personnelMode, setPersonnelMode] = usePersistentValue(
    activeId ? `treasury_personnel_mode_${activeId}` : 'treasury_personnel_mode', 'people');
  // Postes ouverts manuels (étape Point de départ) → pilotent les lignes créances
  // ouvertes / dettes ouvertes du plan de liquidité. Persisté par scénario.
  const [openingManual, setOpeningManual] = usePersistentValue(
    activeId ? `treasury_opening_manual_${activeId}` : 'treasury_opening_manual', OPENING_DEF);
  // Saisie personnel manuelle (onglet Charges personnel, mode Manuel) → pilote
  // les charges de personnel du plan quand personnelMode === 'manual'. Par scénario.
  const [personnelManual, setPersonnelManual] = usePersistentValue(
    activeId ? `treasury_personnel_manual_${activeId}` : 'treasury_personnel_manual', PERSONNEL_DEF);
  // Échéancier des charges sociales : replié par défaut dans « Charges personnel ».
  const [showEcheancier, setShowEcheancier] = useState(false);
  // Postes ouverts : positions stockées par compte (inclusion + profil). Le
  // montant est résolu en direct depuis le bilan (bsViewByYear).
  const [openingPositions, setOpeningPositions] = useState([]);
  // HR data piped into Cash Outlook for the "Salaires nets versés" line.
  const [hrContracts, setHrContracts]       = useState([]);
  const [hrMovements, setHrMovements]       = useState([]);
  const [hrCsRateEmpl, setHrCsRateEmpl]     = useState(0);
  const [hrLppRates, setHrLppRates]         = useState([]);
  const [csLignes, setCsLignes]             = useState([]);
  // Lignes de charges sociales de l'année N+1 (onglet Charges sociales = 24 mois).
  const [csLignesN1, setCsLignesN1]         = useState([]);
  // Annual extras for the scenario start_year and start_year+1 (a 12-month
  // scenario can straddle two calendar years). Each map is { sumVarComp,
  // sumBonus, sumOtherExpenses } across all hr_people for that year.
  const [annualExtrasByYear, setAnnualExtrasByYear] = useState({});
  // Liquidités N-1 du bilan (Financials), par année civile → trajectoire
  // pointillée du Cash Outlook. { [year]: [12 soldes fin de mois] }.
  const [bsLiquidityByYear, setBsLiquidityByYear] = useState({});
  // Vues bilan complètes (avec comptes) pour l'année d'arrêté des postes ouverts.
  const [bsViewByYear, setBsViewByYear] = useState({});

  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  // ── Loaders individuels (utilisés en refresh ciblé) ─────────────────────
  const refreshScenarios = useCallback(async () => {
    if (!tenantId) return null;
    const r = await listScenarios(tenantId);
    if (r.error) { setError(r.error); return null; }
    setScenarios(r.data || []);
    return r.data || [];
  }, [tenantId]);

  const refreshScenario = useCallback(async () => {
    const list = await refreshScenarios();
    if (list && activeId) {
      setScenario(list.find(s => s.id === activeId) || null);
    }
  }, [refreshScenarios, activeId]);

  const refreshRevenues = useCallback(async () => {
    if (!tenantId || !activeId) return;
    const r = await listRevenues(tenantId, activeId);
    if (!r.error) setRevenues(r.data || []);
  }, [tenantId, activeId]);
  const refreshVariables = useCallback(async () => {
    if (!tenantId || !activeId) return;
    const r = await listVariableExpenses(tenantId, activeId);
    if (!r.error) setVariables(r.data || []);
  }, [tenantId, activeId]);
  const refreshIndirectCosts = useCallback(async () => {
    if (!tenantId || !scenario?.start_year) return;
    const r = await listIndirectCosts(tenantId, scenario.start_year);
    if (!r.error) setIndirectCosts(r.data || []);
  }, [tenantId, scenario?.start_year]);
  const refreshOneOff = useCallback(async () => {
    if (!tenantId || !activeId) return;
    const r = await listOneOffExpenses(tenantId, activeId);
    if (!r.error) setOneOff(r.data || []);
  }, [tenantId, activeId]);
  const refreshPayrolls = useCallback(async () => {
    if (!tenantId || !activeId) return;
    const r = await listPayrollChanges(tenantId, activeId);
    if (!r.error) setPayrollChanges(r.data || []);
  }, [tenantId, activeId]);
  const refreshBonuses = useCallback(async () => {
    if (!tenantId || !activeId) return;
    const r = await listBonuses(tenantId, activeId);
    if (!r.error) setBonuses(r.data || []);
  }, [tenantId, activeId]);
  const refreshSocialRates = useCallback(async () => {
    if (!tenantId || !activeId) return;
    const r = await listSocialRates(tenantId, activeId);
    if (!r.error) setSocialRates(r.data || []);
  }, [tenantId, activeId]);
  const refreshInsurances = useCallback(async () => {
    if (!tenantId || !activeId) return;
    const r = await listInsurances(tenantId, activeId);
    if (!r.error) setInsurances(r.data || []);
  }, [tenantId, activeId]);
  // Postes ouverts rattachés à l'année (start_year du plan), plus au scénario.
  const refreshOpeningPositions = useCallback(async () => {
    if (!tenantId || !scenario?.start_year) { setOpeningPositions([]); return; }
    const r = await listOpeningPositions(tenantId, scenario.start_year);
    if (!r.error) setOpeningPositions(r.data || []);
  }, [tenantId, scenario?.start_year]);
  useEffect(() => { refreshOpeningPositions(); }, [refreshOpeningPositions]);
  // Recharge les lignes de charges sociales (N et N+1) après édition du phasing
  // → recalcule assurancesMonthly (cashModel) et le tableau Charges sociales.
  const reloadCsLignes = useCallback(() => {
    if (!tenantId || !scenario?.start_year) return;
    const y = scenario.start_year;
    getChargesSociales(tenantId, y)
      .then((r) => { if (!r.error) setCsLignes(r.data || []); })
      .catch(() => {});
    getChargesSociales(tenantId, y + 1)
      .then((r) => { if (!r.error) setCsLignesN1(r.data || []); })
      .catch(() => {});
  }, [tenantId, scenario?.start_year]);

  // ── Init : liste des scénarios pour le tenant ───────────────────────────
  // Modèle « base fixe + instantanés » : un plan de référence (type='base')
  // existe toujours — créé automatiquement au premier accès. L'utilisateur ne
  // crée pas de scénario ; il fige des instantanés (snapshots) à la demande.
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      const r = await listScenarios(tenantId);
      if (cancelled) return;
      if (r.error) { setError(r.error); return; }
      let list = r.data || [];
      if (list.length === 0) {
        // Départ ancré sur le dernier mois clôturé (bilan d'ouverture) si défini.
        const anchor = startFromLastClosed(activeTenant?.last_closed_period);
        const c = await createScenario(
          tenantId,
          scenarioToApiCreate({ name: p('Plan de référence'), type: 'base', ...(anchor || {}) }),
        );
        if (cancelled) return;
        if (c.error) { setError(c.error); return; }
        list = [c.data];
      }
      setScenarios(list);
      if (!activeId) {
        const base = list.find(s => s.type === 'base') || list[0];
        setActiveId(base.id);
      }
    })().catch(e => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [tenantId]);

  // ── Charge scénario + sous-entités quand activeId change ────────────────
  // Note : `indirect_costs` est tenant × year (table centrale), donc rechargé
  // séparément dès qu'on connaît `scenario.start_year` (voir useEffect en dessous).
  useEffect(() => {
    if (!tenantId || !activeId) {
      setScenario(null);
      setRevenues([]); setVariables([]); setIndirectCosts([]); setOneOff([]);
      setPayrollChanges([]); setBonuses([]); setSocialRates([]); setInsurances([]);
      setOpeningPositions([]);
      return;
    }
    setLoading(true);
    // Postes ouverts non chargés ici : ils suivent scenario.start_year (effet
    // dédié via refreshOpeningPositions), plus l'activeId du scénario.
    Promise.all([
      listScenarios(tenantId),
      listRevenues(tenantId, activeId),
      listVariableExpenses(tenantId, activeId),
      listOneOffExpenses(tenantId, activeId),
      listPayrollChanges(tenantId, activeId),
      listBonuses(tenantId, activeId),
      listSocialRates(tenantId, activeId),
      listInsurances(tenantId, activeId),
    ]).then(([sList, rRev, rVar, rOne, rPay, rBon, rSoc, rIns]) => {
      const scen = (sList.data || []).find(s => s.id === activeId);
      setScenario(scen);
      setRevenues(rRev.data || []);
      setVariables(rVar.data || []);
      setOneOff(rOne.data || []);
      setPayrollChanges(rPay.data || []);
      setBonuses(rBon.data || []);
      setSocialRates(rSoc.data || []);
      setInsurances(rIns.data || []);
      setError(null);
    }).catch(e => setError(e.message))
    .finally(() => setLoading(false));
  }, [tenantId, activeId]);

  // ── Charges indirectes : table centrale tenant × year ───────────────────
  useEffect(() => {
    if (!tenantId || !scenario?.start_year) { setIndirectCosts([]); return; }
    listIndirectCosts(tenantId, scenario.start_year)
      .then(r => { if (!r.error) setIndirectCosts(r.data || []); })
      .catch(e => setError(e.message));
  }, [tenantId, scenario?.start_year]);

  // ── HR data for personnel cash projection ───────────────────────────────
  useEffect(() => {
    if (!tenantId || !scenario?.start_year) {
      setHrContracts([]); setHrMovements([]); setHrCsRateEmpl(0); setHrLppRates([]);
      setCsLignes([]); setCsLignesN1([]);
      setAnnualExtrasByYear({});
      return;
    }
    const yearN = scenario.start_year;
    const yearN1 = yearN + 1;
    Promise.all([
      listHrContracts(tenantId),
      listPayrollMovements(tenantId),
      peopleCostSynthesis(tenantId, yearN, 'all'),
      listHrLppRates(tenantId),
      getChargesSociales(tenantId, yearN).catch(() => ({ data: [] })),
      listHrAnnualExtras(tenantId, yearN).catch(() => ({ data: [] })),
      listHrAnnualExtras(tenantId, yearN1).catch(() => ({ data: [] })),
      getChargesSociales(tenantId, yearN1).catch(() => ({ data: [] })),
    ]).then(([rC, rM, rS, rLpp, rCs, rEx, rEx1, rCs1]) => {
      if (!rC.error) setHrContracts(rC.data || []);
      if (!rM.error) setHrMovements(rM.data || []);
      const synth = rS?.data || rS;
      setHrCsRateEmpl(Number(synth?.cs_fallback_rate_empl) || 0);
      if (!rLpp.error) setHrLppRates(rLpp.data || []);
      setCsLignes(rCs?.data || []);
      setCsLignesN1(rCs1?.data || []);
      const aggregate = (rows) => {
        const arr = rows?.data || rows || [];
        return arr.reduce((acc, r) => {
          // extras_cs_total = Σ compléments soumis aux charges sociales
          // (hr_extra_items). Lissage cash ÷12 inchangé ; bonus reste à 0.
          acc.varComp += Number(r.extras_cs_total || 0);
          // People > Expenses : fixed + paid indemnities + other expenses
          acc.expenses += Number(r.fixed_indemnities || 0)
                        + Number(r.paid_indemnities || 0)
                        + Number(r.other_expenses || 0);
          return acc;
        }, { varComp: 0, bonus: 0, expenses: 0 });
      };
      setAnnualExtrasByYear({
        [yearN]:  aggregate(rEx),
        [yearN1]: aggregate(rEx1),
      });
    }).catch(e => setError(e.message));
  }, [tenantId, scenario?.start_year]);

  // ── Liquidités N-1 du bilan (pour la trajectoire de comparaison) ─────────
  // On lit la ligne « Liquidités » des actifs circulants au bilan de l'année
  // précédant le forecast. Si le plan démarre en cours d'année (straddle deux
  // années civiles), on charge aussi le bilan de start_year pour couvrir les
  // mois de start_year+1. Donnée absente = absente (pas de repli).
  useEffect(() => {
    const sy = scenario?.start_year;
    if (!tenantId || !sy) { setBsLiquidityByYear({}); setBsViewByYear({}); return undefined; }
    const sm = scenario?.start_month ?? 0;
    // sy-1 et sy couvrent à la fois la trajectoire N-1 et l'année d'arrêté des
    // postes ouverts (mois précédant le début : déc N-1 si janvier, sinon dans sy).
    const years = sm > 0 ? [sy - 1, sy] : [sy - 1];
    const extractLiquidity = (view) => {
      const row = (view?.rows || []).find(
        (r) => r.type === 'detail' && r.std_key === 'liquidity',
      );
      if (!row || !row.months) return null;
      return Array.from({ length: 12 }, (_, m) => {
        const v = Number(row.months[String(m + 1)]);
        return Number.isFinite(v) ? v : null;
      });
    };
    let cancelled = false;
    (async () => {
      const liqOut = {};
      const viewOut = {};
      await Promise.all(years.map(async (yr) => {
        const r = await getBsView(tenantId, yr);
        if (r.error) return;
        viewOut[yr] = r.data;
        const liq = extractLiquidity(r.data);
        if (liq) liqOut[yr] = liq;
      }));
      if (!cancelled) { setBsLiquidityByYear(liqOut); setBsViewByYear(viewOut); }
    })();
    return () => { cancelled = true; };
  }, [tenantId, scenario?.start_year, scenario?.start_month]);

  // ── Postes ouverts résolus (positions stockées × bilan d'arrêté) ────────
  // Alimente le calcul cash + l'onglet « Point de départ ». L'édition des
  // postes ouverts (Sélection PO) vit dans le hub Prévisionnel de Finance.
  const resolvedOpening = useMemo(
    () => resolveOpeningPositions({ scenario, positions: openingPositions, bsViewByYear }),
    [scenario, openingPositions, bsViewByYear],
  );

  // ── Calcul partagé du Cash Outlook (moteur + projections HR) ─────────────
  // Extrait dans engine/cashModel pour être réutilisé à l'identique par le
  // print (CashForecastPrint) → chiffres garantis identiques écran/PDF.
  const cashModel = useMemo(() => {
    const empty = { calc: null, personnelProjection: null, chargesSocialesMonthly: null, assurancesMonthly: null, annualExtrasMonthly: null };
    if (!scenario) return empty;
    // Créances / dettes ouvertes = étape « Postes ouverts » (saisie manuelle),
    // phasées sur M1/M2/M3. Le solde initial reste tiré du bilan (resolvedOpening).
    const { receivables, payables } = openingToLines(openingManual, scenario.start_month ?? 0);
    // Charges de personnel : mode Manuel → séries dérivées de la saisie manuelle
    // (sinon projection People via HR contracts, comportement inchangé).
    const manualPers = personnelMode === 'manual'
      ? computeManualPersonnel(personnelManual, scenario.start_month ?? 0) : null;
    try {
      return computeCashModel({
        scenario, tenant: activeTenant, revenues, variables, indirectCosts, oneOff,
        payrollChanges, bonuses, socialRates, insurances,
        openingReceivables: receivables,
        openingPayables: payables,
        initialBalance: resolvedOpening.initialBalance,
        hrContracts, hrMovements, hrCsRateEmpl, hrLppRates,
        csLignes, annualExtrasByYear,
        personnelOverride: manualPers,
      });
    } catch (e) {
      console.error('Calc error', e);
      setError(t('scenarii:treasury.errorCalc', { message: e.message }));
      return empty;
    }
  }, [scenario, activeTenant, revenues, variables, indirectCosts, oneOff, payrollChanges, bonuses, socialRates, insurances, resolvedOpening, openingManual, personnelMode, personnelManual, hrContracts, hrMovements, hrCsRateEmpl, hrLppRates, csLignes, annualExtrasByYear]);

  const { calc, personnelProjection, chargesSocialesMonthly, assurancesMonthly, annualExtrasMonthly } = cashModel;

  // Changer de scénario actif remet la comparaison à zéro (contexte différent).
  useEffect(() => { setCompareIds([]); }, [activeId]);

  // Resynchronise la durée de projection sur le scénario chargé (à chaque
  // changement de scénario). Dépend de l'id seul → une édition optimiste locale
  // (setHorizon, même id) ne réécrase pas l'état pendant la persistance.
  useEffect(() => { setHorizonLocal(scenario?.horizon ?? 12); }, [scenario?.id]); // eslint-disable-line

  // Change la durée : maj optimiste immédiate (curseur fluide) + PATCH serveur
  // (attribut du scénario). Bloqué sur un scénario verrouillé.
  const setHorizon = useCallback((h) => {
    const v = Math.min(12, Math.max(1, Math.round(Number(h) || 12)));
    setHorizonLocal(v);
    if (!tenantId || !activeId || scenario?.locked) return;
    updateScenario(tenantId, activeId, { horizon: v }).then((r) => {
      if (r.error || !r.data) return;
      setScenarios((prev) => prev.map((s) => (s.id === activeId ? r.data : s)));
      setScenario((prev) => (prev && prev.id === activeId ? { ...prev, horizon: v } : prev));
    });
  }, [tenantId, activeId, scenario?.locked]);

  // Chargement paresseux des courbes de solde des scénarios comparés : on rejoue
  // le moteur cash (côté client, source unique) pour chaque id coché, et on ne
  // conserve que la série de solde mensuel. Mémoïsé par id (compareCacheRef).
  useEffect(() => {
    let alive = true;
    if (!tenantId || compareIds.length === 0) { setCompareSeries([]); return undefined; }
    (async () => {
      const out = [];
      for (const id of compareIds) {
        let solde = compareCacheRef.current[id];
        if (!solde) {
          try {
            const data = await loadCashForecastData(tenantId, scenario?.start_year, activeTenant, id);
            const res = computeForecastSeries(data);
            solde = res?.s?.effectiveSoldeMonthly || null;
            if (solde) compareCacheRef.current[id] = solde;
          } catch { solde = null; }
        }
        const sc = scenarios.find((s) => s.id === id);
        if (solde) out.push({ id, name: sc?.name || p('Scénario'), solde });
      }
      if (alive) setCompareSeries(out);
    })();
    return () => { alive = false; };
  }, [tenantId, compareIds, scenario?.start_year, activeTenant, scenarios]);

  // Fige un instantané (copie verrouillée, lecture seule) du plan de travail
  // couramment affiché — ou du plan de référence si on consulte un instantané.
  async function handleSnapshot() {
    const activeScen = scenarios.find(s => s.id === activeId);
    const source = (activeScen && activeScen.type !== 'snapshot')
      ? activeScen
      : (scenarios.find(s => s.type === 'base') || scenarios.find(s => s.type !== 'snapshot'));
    if (!source) return;
    const suggested = `${source.name} — ${new Date().toLocaleDateString('fr-CH')}`;
    const name = window.prompt(t('scenarii:treasury.promptSnapshotName'), suggested);
    if (name === null) return;  // annulé
    const r = await snapshotScenario(tenantId, source.id, name.trim() || undefined);
    if (r.error) { setError(r.error); return; }
    await refreshScenarios();
    addToast && addToast(t('scenarii:treasury.toastSnapshotSaved'));
  }

  // Duplique le scénario courant en un plan de travail éditable (＋ Nouveau
  // scénario). La copie devient le scénario actif.
  async function handleDuplicate() {
    const source = scenarios.find((s) => s.id === activeId) || scenarios[0];
    if (!source) return;
    const r = await duplicateScenario(tenantId, source.id);
    if (r.error) { setError(r.error); return; }
    await refreshScenarios();
    if (r.data?.id) setActiveId(r.data.id);
    addToast && addToast(t('scenarii:treasury.toastDuplicated'));
  }

  // Renommage inline d'un scénario (barre de scénarios).
  async function handleRename(id, name) {
    const target = id || activeId;
    if (!tenantId || !target || !name || !name.trim()) return;
    const r = await updateScenario(tenantId, target, { name: name.trim() });
    if (r.error) { setError(r.error); return; }
    setScenarios((prev) => prev.map((s) => (s.id === target ? r.data : s)));
    if (target === activeId) setScenario(r.data);
    // Le nom d'un scénario comparé peut changer → on rafraîchit son cache d'affichage.
    compareCacheRef.current[target] && setCompareSeries((prev) =>
      prev.map((c) => (c.id === target ? { ...c, name: r.data.name } : c)));
  }

  async function handleUpdateActive(payload) {
    if (!tenantId || !activeId) return;
    const r = await updateScenario(tenantId, activeId, payload);
    if (r.error) { setError(r.error); return; }
    setScenarios((prev) => prev.map((s) => (s.id === activeId ? r.data : s)));
    setScenario(r.data);
  }

  async function handleDelete() {
    if (!activeId) return;
    if (!window.confirm(t('scenarii:treasury.confirmDelete'))) return;
    const r = await deleteScenario(tenantId, activeId);
    if (r.error) { setError(r.error); return; }
    const remaining = scenarios.filter(s => s.id !== activeId);
    setScenarios(remaining);
    setActiveId(remaining[0]?.id || null);
  }

  function reloadAll() {
    refreshScenario();
    refreshRevenues(); refreshVariables(); refreshIndirectCosts(); refreshOneOff();
    refreshPayrolls(); refreshBonuses(); refreshSocialRates(); refreshInsurances();
  }

  async function handleReset() {
    if (!scenario) return;
    if (!window.confirm(t('scenarii:treasury.confirmReset', { name: scenario.name }))) return;
    const r = await resetScenario(tenantId, scenario.id);
    if (r.error) { setError(r.error); return; }
    reloadAll();
  }

  // Libellé période du plan (12 mois) : « Janvier 2026 → Décembre 2026 ».
  const currentYear = new Date().getFullYear();
  const periodLabel = (() => {
    if (!scenario) return '';
    const sm = scenario.start_month ?? 0;
    const sy = scenario.start_year ?? currentYear;
    const em = (sm + 11) % 12;
    const ey = sy + Math.floor((sm + 11) / 12);
    return `${t(`common:monthsFull.${sm + 1}`)} ${sy} → ${t(`common:monthsFull.${em + 1}`)} ${ey}`;
  })();

  // Onglet « Point de départ » : options d'année + libellé du mois d'arrêté du
  // bilan d'ouverture (mois précédant le 1er mois du plan).
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1];
  const soldesPeriodLabel = (() => {
    const period = resolvedOpening?.period;
    return period ? `${t(`common:monthsFull.${period.month}`)} ${period.year}` : '—';
  })();

  /* Rendu d'un onglet — à l'écran ET pour l'export Excel : le classeur monte les
     onglets non affichés hors écran, il lui faut donc pouvoir rendre n'importe
     lequel à la demande (extrait du corps de la page le 2026-08-23). */
  const renderTab = (id) => (!scenario ? null : (
    <>
            {(id === 'synthesis' || id === 'detail') && (
              <SynthesisTab
                scenario={scenario}
                calc={calc}
                revenues={revenues}
                variables={variables}
                personnelProjection={personnelProjection}
                chargesSocialesMonthly={chargesSocialesMonthly}
                assurancesMonthly={assurancesMonthly}
                annualExtrasMonthly={annualExtrasMonthly}
                bsLiquidityByYear={bsLiquidityByYear}
                detailMode={id === 'detail'}
                horizon={horizon}
                scenarios={scenarios}
                compareIds={compareIds}
                onCompareChange={setCompareIds}
                compareSeries={compareSeries}
              />
            )}

            {id === 'revenues' && (
              <RevenuesTab
                tenantId={tenantId} scenarioId={scenario.id}
                scenario={scenario}
                locked={scenario.locked}
                revenues={revenues}
                variables={variables}
                onChange={refreshRevenues}
                onChangeVariables={refreshVariables}
              />
            )}

            {id === 'purchases' && (
              <PurchasesTab
                tenantId={tenantId} scenarioId={scenario.id}
                locked={scenario.locked}
                revenues={revenues}
                variables={variables}
                onChange={refreshVariables}
                startMonth={scenario.start_month ?? 0}
                horizon={horizon}
              />
            )}

            {id === 'starting' && (
              <StartingStepper
                resolved={resolvedOpening}
                scenario={scenario}
                periodLabel={soldesPeriodLabel}
                startMonth={scenario.start_month ?? 0}
                yearOptions={yearOptions}
                lastClosed={activeTenant?.last_closed_period}
                locked={scenario.locked}
                onPatch={handleUpdateActive}
                horizon={horizon}
                onHorizonChange={setHorizon}
                tenantId={tenantId}
                indirectCosts={indirectCosts}
                onChangeIndirect={refreshIndirectCosts}
                openingManual={openingManual}
                onOpeningChange={setOpeningManual}
                personnelMode={personnelMode}
                onPersonnelModeChange={setPersonnelMode}
              />
            )}

            {id === 'personnel' && (
              <div className="tres-personnel-tab">
                {personnelMode === 'manual' ? (
                  <PersonnelManualForm scenario={scenario} locked={scenario.locked}
                    form={personnelManual} onChange={setPersonnelManual} />
                ) : (
                  <>
                    <PersonnelProjectionView
                      scenario={scenario}
                      calc={calc}
                      revenues={revenues}
                      variables={variables}
                      personnelProjection={personnelProjection}
                      chargesSocialesMonthly={chargesSocialesMonthly}
                      assurancesMonthly={assurancesMonthly}
                      annualExtrasMonthly={annualExtrasMonthly}
                      bsLiquidityByYear={bsLiquidityByYear}
                      horizon={horizon}
                    />

                    {/* Échéancier des charges sociales (People) — replié, ouvert au bouton. */}
                    <div className="tres-echeancier">
                      <button
                        type="button"
                        className="tres-echeancier__toggle"
                        onClick={() => setShowEcheancier((v) => !v)}
                        aria-expanded={showEcheancier}
                      >
                        {showEcheancier ? '▾' : '▸'} {t('scenarii:treasury.socialScheduleToggle')}
                      </button>
                      {showEcheancier && (
                        <SocialPhasingTab
                          tenantId={tenantId}
                          year={scenario.start_year}
                          allYears
                          addToast={addToast}
                          locked={scenario.locked}
                        />
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

    </>
  ));

  /* Pas d'export sur la Simulation 12M (décision Olivier du 23.08.2026) : ni
     Excel, ni PDF. La page ne déclare donc rien au registre, et l'icône
     télécharger de la barre reste ATTÉNUÉE — l'état normal d'un écran sans
     sortie, pas une absence à interpréter. C'est un outil de simulation à
     N scénarios, pas un état à diffuser. */


  /* Garde « pas de client » — placé APRÈS les hooks, jamais avant : un retour
     anticipé au-dessus d'eux les rendrait conditionnels, et l'ordre des hooks
     changerait d'un rendu à l'autre. Tout ce qui précède supporte l'absence de
     client (scenario null → renderTab ne rend rien). */
  if (!tenantId) {
    return (
      <PageShell framed className="treasury" title={t('scenarii:treasury.planTitle')}>
        <div className="treasury__placeholder">{t('scenarii:treasury.selectTenant')}</div>
      </PageShell>
    );
  }

  return (
    <PageShell
      framed
      className="treasury"
      title={t('scenarii:treasury.planTitle')}
    >
      <ScenarioBar
        scenarios={scenarios}
        activeId={activeId}
        onSelect={setActiveId}
        onSnapshot={handleSnapshot}
        onDuplicate={handleDuplicate}
        onRename={handleRename}
        onDelete={handleDelete}
        onReset={handleReset}
        resetDisabled={!scenario || scenario.locked}
        periodLabel={periodLabel}
      />

      <Tabs
        tabs={TAB_ITEMS}
        active={activeTab}
        flow
        onChange={(id) => onTabChange && onTabChange(id)}
      />

      {error && <div className="treasury__error">{error} <button onClick={() => setError(null)}>×</button></div>}

      {!scenario && !loading && (
        <div className="treasury__placeholder">
          {t('scenarii:treasury.noScenario')}
        </div>
      )}

      {scenario && (
        <div className="treasury__layout">
          <main className="treasury__content">
            {renderTab(activeTab)}
          </main>
        </div>
      )}

    </PageShell>
  );
}
